import { useParams } from 'react-router-dom';

import { RunApprovalPopoverContent } from './run-approval-popover-content';
import { Permission } from '../../../../backend/src/auth/permissions/permission';
import { RunType } from '../../../../backend/src/run/run-type';
import { RunStatus, Interval } from '../../../../backend/src/types/types';
import { blockedReasonMessage } from '../../../../backend/src/utils/billing-utils';
import { useMatterProcessingStore } from '../stores/matter-processing-store';
import { useSubmitRunApproval } from '@/api/mutations/useSubmitRunApproval';
import { useUserContext } from '@/Contexts/User';
import formatDate from '@/helpers/formatDate';
import { getRunType, getRunCreditEstimate, formatRunCredits } from '@/helpers/runs';
import { useHasPermission } from '@/hooks/useHasPermission';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/new/components/shadcn/popover';
import { PipelineProcessingStageDescriptions, ProcessingStatus } from '@/new/lib/pipeline-status';

const MINUTES_PER_HOUR = 60;
const MINUTES_PER_DAY = 24 * MINUTES_PER_HOUR;

/** Total minutes in an interval, for sorting and arithmetic. Null intervals stay null. */
export function intervalToMinutes(interval?: Interval | null): number | null {
  if (!interval) return null;
  return (interval.days ?? 0) * MINUTES_PER_DAY + (interval.hours ?? 0) * MINUTES_PER_HOUR + (interval.minutes ?? 0);
}

export function formatDurationEstimate(durationEstimate?: Interval | null): string {
  if (!durationEstimate) return '';
  const parts: string[] = [];
  const days = durationEstimate?.days ?? 0;
  const hours = durationEstimate?.hours ?? 0;
  const minutes = durationEstimate?.minutes ?? 0;
  if (days > 0) parts.push(`${days} day${days !== 1 ? 's' : ''}`);
  if (hours > 0) parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
  if (minutes > 0) parts.push(`${minutes} minute${minutes !== 1 ? 's' : ''}`);
  return parts.join(' ');
}

export function ProcessingStatusBadge({ allDocumentsCaseId }: { allDocumentsCaseId: string | undefined }) {
  const { matterId } = useParams();
  const { hasPermission } = useHasPermission();
  const { user } = useUserContext();

  // Check if user has permission to approve runs
  const canApproveRuns = hasPermission(Permission.RunApprove);
  const blockedMessage = user?.isBlocked ? blockedReasonMessage(user.blockedReason, user.blockedUntil) : null;

  const { mutateAsync: submitRunApproval, isLoading: isSubmittingApproval } = useSubmitRunApproval(
    allDocumentsCaseId ?? '',
    matterId ?? '',
  );

  const processingState = useMatterProcessingStore((state) =>
    matterId ? state.getProcessingState(matterId) : undefined,
  );

  if (!matterId || !allDocumentsCaseId) return null;
  const allProcessingRuns = processingState?.allProcessingRuns ?? [];

  // Separate awaiting approval runs from processing runs
  const awaitingApprovalRuns = allProcessingRuns.filter((run) => run.status === RunStatus.AwaitingApproval);
  const processingRuns = allProcessingRuns.filter((run) => run.status !== RunStatus.AwaitingApproval);

  const handleApprove = async (runId: string) => {
    await submitRunApproval({ runId, approvalGranted: true });
  };

  const handleDeny = async (runId: string) => {
    await submitRunApproval({ runId, approvalGranted: false });
  };

  // Determine badge appearance based on runs
  const hasAwaitingApproval = awaitingApprovalRuns.length > 0;
  const hasProcessing = processingRuns.length > 0;

  const approvalCount = awaitingApprovalRuns.length;
  const processingCount = processingRuns.length;
  const approvalText = `${approvalCount} Approval${approvalCount > 1 ? 's' : ''}`;

  let badgeText = '';
  if (hasAwaitingApproval && hasProcessing) {
    badgeText = `${approvalText}, ${processingCount} Processing`;
  } else if (hasAwaitingApproval) {
    badgeText = `${approvalText} Required`;
  } else {
    badgeText = `${processingCount} Processing`;
  }

  const badgeClassName = hasAwaitingApproval
    ? 'flex items-center gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-700 hover:bg-amber-100 hover:text-amber-800'
    : 'flex items-center gap-1.5 rounded-md border border-sky-300 bg-sky-50 text-sky-600 px-2 py-1 hover:bg-sky-100 hover:text-sky-700';

  const badgeIcon = hasAwaitingApproval ? (
    <Icon className="lucide--shield-alert text-amber-600" />
  ) : (
    <Icon className="lucide--loader-2 animate-spin text-sky-600 ease-in-out" />
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline" className={badgeClassName}>
          {badgeIcon}
          {badgeText}
          <Icon className="lucide--arrow-right" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={10} className="w-96 max-w-96 overflow-y-auto rounded-lg p-0">
        <div className="flex flex-col">
          {awaitingApprovalRuns.map((run, index) => {
            const credits = getRunCreditEstimate(run);
            const isAllDocumentsCaseRun = run.caseId === allDocumentsCaseId;
            const runType = getRunType(run, isAllDocumentsCaseRun);

            return (
              <div key={run.id} className={`${index > 0 ? 'mt-2 border-t-2 border-gray-300' : ''} bg-gray-50/30`}>
                <RunApprovalPopoverContent
                  credits={credits}
                  pagesPerCredit={run.pagesPerCredit}
                  durationEstimate={run.durationEstimate}
                  isSubmittingApproval={isSubmittingApproval}
                  onApprove={() => handleApprove(run.id)}
                  onDeny={() => handleDeny(run.id)}
                  runType={runType}
                  subsetName={run.subsetName}
                  requiresAdminApproval={run.requiresAdminApproval}
                  canApprove={canApproveRuns || run.createdById === user?.id}
                  blockedMessage={blockedMessage}
                />
              </div>
            );
          })}

          {/* Show processing runs */}
          {processingRuns.map((run, index) => {
            const stage = run.status as ProcessingStatus;
            const formattedEta = run.completedAtEstimate ? formatDate(run.completedAtEstimate, true, true) : null;
            const isAllDocumentsCaseRun = run.caseId === allDocumentsCaseId;
            const runType = getRunType(run, isAllDocumentsCaseRun);
            const credits = getRunCreditEstimate(run);

            // Recontextualising a subset's facts doesn't reprocess pages or move
            // through the page pipeline, so credits and stage don't apply — show
            // only the run title and message.
            const isRecontextualisingSubset = run.type === RunType.UpdateSynopsis && !isAllDocumentsCaseRun;

            const processingMessage = isAllDocumentsCaseRun ? (
              <p>Your Matter is being processed.</p>
            ) : (
              <p>
                Recontextualising facts for <strong>{run.subsetName}</strong>.
              </p>
            );

            // Add border-top and margin if this is not the first run overall
            const isFirstRun = awaitingApprovalRuns.length === 0 && index === 0;
            const separatorClass = isFirstRun ? '' : 'border-t-2 border-gray-300 mt-2';

            return (
              <div key={run.id} className={`${separatorClass} bg-gray-50/30`}>
                <div className="border-b px-4 py-3">
                  <h4 className="font-medium text-gray-800">Processing: {runType}</h4>
                  <div className="text-xs text-gray-500">{processingMessage}</div>
                </div>

                {(!isRecontextualisingSubset || run.completedAtEstimate) && (
                  <div className="p-1">
                    <ul className="my-1.5 pl-3 text-xs text-gray-500 marker:text-gray-300">
                      {!isRecontextualisingSubset && (
                        <li>
                          Estimated credits:{' '}
                          <strong className="font-medium text-gray-700">
                            {credits === null ? 'Unavailable' : formatRunCredits(credits)}
                          </strong>
                        </li>
                      )}
                      {run.completedAtEstimate && (
                        <li>
                          Estimated completion: <strong className="font-medium text-gray-700">{formattedEta}</strong>
                        </li>
                      )}
                      {!isRecontextualisingSubset && stage && PipelineProcessingStageDescriptions[stage] && (
                        <li>
                          Current stage:{' '}
                          <strong className="font-medium text-gray-700">
                            {PipelineProcessingStageDescriptions[stage].title}
                          </strong>
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
